//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

//#region Model Imports

import { Enums } from '../../../models/common';
import { OpDataType, OpDataTypeInGroup } from "../../../models/core";
import { OpDataList } from '../../../models/util';
import { IOpData } from '../../../models/util/interfaces';

//#endregion

//#region Service Imports

import { ConvertHelper, Dictionary, LocalizationHelper, LogHelper, ToastHelper, TypeHelper } from "../../../helpers";
import { ReferenceTypeService } from '../../../services/reference-type';
import { UserSessionService } from '../../../services/user-session';
import { GridService } from '../../../services/grid';

//#endregion

import { IMRSelectGridObjectSelected } from '../select-grid';
import { IIMRSelectGridInputField, IMRSelectGridInputFieldOptions, IMRSelectGridInputFieldInitializedArgs } from '../select-grid/input-field/'

import { IDataTypePanelRule, IIMRDataTypePanel, IMRDataTypePanelAdditionalOptions, IMRDataTypePanelDataTypeGroupsOptions, IMRDataTypePanelDataTypesOptions, IMRDataTypePanelFieldValueChanged, IMRDataTypePanelFormItemOptions, IMRDataTypePanelOpDataTypesOptions, IMRDataTypePanelOptions } from './interfaces'
import {
    DataTypeClassBooleanRule,
    DataTypeClassDateTimeRule,
    DataTypeClassIntegerRule,
    DataTypeClassRealRule,
    DataTypeClassTextRule,
    DefaultRule,
    ReferenceTypeRule,
    ViewReferenceTypeRule
} from './rules'

export class IMRDataTypePanelController implements ng.IComponentController, IIMRDataTypePanel {
    // #region Consts

    private static get LOG_STRING(): string { return '[SIMAX.Common.DataTypePanel] - %s' }
    private static get SELECT_POPOVER_OPTIONS(): string { return 'selectGridPopupOptions' }
    private static get SELECT_POPOVER(): string { return 'selectGridPopup' }

    // #endregion

    // #region Members

    private form: DevExpress.ui.dxForm
    private formOptions: DevExpress.ui.dxFormOptions
    private editedDict: Dictionary<boolean> = new Dictionary<boolean>(null)
    private listOriginalValues: Dictionary<any[]> = new Dictionary<any[]>(null)

    private isLoading: boolean
    private options: IMRDataTypePanelOptions
    private rules: IDataTypePanelRule[]
    private popoverOptions: IMRSelectGridInputFieldOptions<any>[]
    private popovers: IIMRSelectGridInputField<any>[]
    private popoverFormData: { object: any, id: string, key: any }
    private popoverFormDataArray: { object: any, id: string, key: any }[]

    private dataList: OpDataList<IOpData>
    private opDataTypes: IMRDataTypePanelOpDataTypesOptions
    private dataTypes: IMRDataTypePanelDataTypesOptions
    private dataTypeGroups: IMRDataTypePanelDataTypeGroupsOptions

    // #endregion

    // #region Ctor

    public constructor(
        private readonly gridService: GridService,
        private readonly referenceTypeService: ReferenceTypeService,
        private readonly userSessionService: UserSessionService,
    ) {
        this.popoverFormDataArray = []

        // !The order of rules is conditioned by the complexity of the rule, from the most complex to the least complex
        this.rules = [
            new ViewReferenceTypeRule(this.gridService, this.referenceTypeService),
            new ReferenceTypeRule(this.referenceTypeService),
            // new LatitudeLongitudeRule(),
            new DataTypeClassIntegerRule(),
            new DataTypeClassBooleanRule(),
            new DataTypeClassDateTimeRule(),
            new DataTypeClassRealRule(),
            new DataTypeClassTextRule(),
            new DefaultRule()
        ]
    }

    // #endregion

    // #region IComponentController Implementation

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion

    // #region IIMRDataTypePanel Implementation

    public getDataList(): OpDataList<IOpData> {
        const fieldData: any = this.getFormFieldData()
        const dataList: OpDataList<IOpData> = new OpDataList<IOpData>()

        if (this.opDataTypes != null && fieldData != null) {
            _.forEach(this.opDataTypes.params, (opDataType: OpDataType) => {
                const fieldValue: any = fieldData[opDataType.idDataType]

                // If there is multi select
                if (_.isArray(fieldValue)) {
                    const originalValues = this.listOriginalValues.tryGetValue(opDataType.idDataType)

                    if (originalValues != null) {
                        // Create loaded or deleted
                        _.forEach(originalValues, (originalValue: any) => {
                            const opState = _.contains(fieldValue, originalValue) ? Enums.OpChangeState.Loaded : Enums.OpChangeState.Delete

                            dataList.push(this.createIOpData(opDataType, originalValue, opState))
                        })

                        // Create new data
                        _.forEach(fieldValue, (fieldValue: any) => {
                            if (!_.contains(originalValues, fieldValue)) {
                                dataList.push(this.createIOpData(opDataType, fieldValue, Enums.OpChangeState.New))
                            }
                        })
                    }
                }
                else {
                    const opState = this.editedDict.tryGetValue(opDataType.idDataType) ? Enums.OpChangeState.Modified : Enums.OpChangeState.New

                    dataList.push(this.createIOpData(opDataType, fieldValue, opState))
                }
            })
        }

        return dataList
    }

    public validate(): boolean {
        // Validation checks separated into multiple expressions
        // because we want to perform full validation everytime.
        let isValid: boolean = true

        // Don't change order of expressions because if any of left
        // expresions is `false` others are not evaluated.

        if (this.form != null) {
            const validation: DevExpress.ui.dxValidationGroupResult = this.form.validate()

            if (validation != null) {
                isValid = validation.isValid && isValid
            }

            isValid = this.validateSelectPopovers() && isValid
        }

        return isValid
    }

    public reset(): JQueryPromise<void> {
        this.clearFormData()

        this.isLoading = true

        let defferedDataTypePanelFormItems: JQueryPromise<IMRDataTypePanelFormItemOptions>[] = []

        return this
            .getOpDataTypes()
            .then(([params, required]: OpDataType[][]) => {
                this.opDataTypes = {
                    params,
                    required
                }

                if (params != null) {
                    defferedDataTypePanelFormItems = params.map((dataType: OpDataType) => {
                        return this.getFormItem(dataType, this.isOpDataTypeRequired(dataType, required))
                    })
                } else {
                    this.isLoading = false

                    return $.Deferred<void>().resolve().promise()
                }

                return $.when(...defferedDataTypePanelFormItems)
                    .then((...formItems: IMRDataTypePanelFormItemOptions[]) => {
                        this.setPopoverOptions(formItems)
                        this.setFormOptions(formItems)
                    })
                    .fail((error: any) => {
                        const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                        ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                        LogHelper.warn(IMRDataTypePanelController.LOG_STRING, `Unable to create DataTypePanel: ${errorMsg}.`)
                    })
                    .always(() => {
                        this.isLoading = false
                    })
            })
    }

    public setOpDataTypes(opDataTypes: IMRDataTypePanelOpDataTypesOptions): void {
        this.opDataTypes = opDataTypes
    }

    public setDataTypes(dataTypes: IMRDataTypePanelDataTypesOptions): void {
        this.dataTypes = dataTypes
    }

    public setDataTypeGroups(dataTypeGroups: IMRDataTypePanelDataTypeGroupsOptions): void {
        this.dataTypeGroups = dataTypeGroups
    }

    public setDataList(dataList: OpDataList<IOpData>): void {
        this.dataList = dataList || null
    }

    // #endregion

    // #region Form

    private setFormOptions(formItems: IMRDataTypePanelFormItemOptions[]) {
        if (this.formOptions != null) {
            this.form.option('items', formItems)
        } else {
            this.formOptions = {
                items: (formItems as DevExpress.ui.dxFormSimpleItem[]),
                colCount: this.options.columnCount ? this.options.columnCount : 3,
                scrollingEnabled: true,

                onInitialized: (e: any) => {
                    this.form = e.component
                },

                onContentReady: (args: any) => {
                    this.onFormContentReady()
                },

                onFieldDataChanged: (e: any) => {
                    if (this.editedDict.containsKey(e.dataField)) {
                        this.editedDict.addOrUpdate(e.dataField, true)
                    }
                    else {
                        this.editedDict.add(e.dataField, null)

                        if (_.isArray(e.value)) {
                            this.listOriginalValues.add(e.dataField, e.value)
                        }
                    }

                    this.onFieldValueChanged({
                        controller: this,
                        dataField: e.dataField,
                        value: e.value
                    })
                }
            }
        }
    }

    private getFormFieldData(): any {
        let defaultPropertiesValid: boolean = false
        let popoversValid: boolean = false

        let resultObj: any = {}

        const validation: DevExpress.ui.dxValidationGroupResult = this.form.validate()

        if (validation != null && validation.isValid) {
            defaultPropertiesValid = true

            resultObj = _.extend(resultObj, this.form.option('formData'))
        }

        if (_.size(this.popoverFormDataArray) > 0) {
            const elements: any = this.popoverFormDataArray.map((element: { object: any, id: string, key: any }) => { return element })

            if (this.validateSelectPopovers()) {
                popoversValid = true

                elements
                    .map(((element: any) => {
                        const obj: any = {}
                        obj[element.key] = element.object.getReferenceKey()
                        return obj
                    }))
                    .forEach((pair: any) => {
                        resultObj = _.extend(resultObj, pair)
                    })
            }
        }

        if (
            defaultPropertiesValid
            && popoversValid
        ) {
            return resultObj
        }

        return null
    }

    // #endregion

    // #region validateSelectPopovers

    private validateSelectPopovers(): boolean {
        let isValid = true

        if (this.popovers != null) {
            const popovers: IIMRSelectGridInputField<any>[] = _.values(this.popovers)

            if (_.size(popovers)) {
                for (let popover of popovers) {
                    if (!popover.isValid()) {
                        isValid = false
                        break
                    }
                }
            }
        }

        return isValid
    }

    // #endregion

    // #region Private Methods

    private clearFormData(): void {
        this.editedDict = new Dictionary<boolean>(null)
        this.popoverOptions = []
        this.popovers = []
        this.popoverFormDataArray = []

        if (this.form != null) {
            this.form.option('formData', []);
        }
    }

    private getFormItem(param: OpDataType, isRequired: boolean = false): JQueryPromise<IMRDataTypePanelFormItemOptions> {
        const ruleDeferred: JQueryDeferred<IDataTypePanelRule> = $.Deferred<IDataTypePanelRule>()

        const checkRule = (index: number): void => {
            if (this.rules.length > index) {
                const currentRule = this.rules[index]

                currentRule.canHandle(param)
                    .then((canHandle: boolean) => {
                        if (canHandle) {
                            ruleDeferred.resolve(currentRule)
                        } else {
                            checkRule(index += 1)
                        }
                    })
            } else {
                ruleDeferred.resolve(null)
            }
        }

        checkRule(0)

        return ruleDeferred.then((rule: IDataTypePanelRule) => {
            if (rule != null) {
                return rule.getItemFormFieldOptions(
                    param,
                    this.getOpDataTypeAdditionalOptions(param),
                    isRequired,
                    this.getOpDataTypeData(param)
                )
            }

            return null
        })
    }

    // #endregion

    // #region Location Select

    private onLocationSelected = (latitude: number, longitude: number): void => {
        const formData: Object = this.form.option('formData')
        if (formData) {
            formData['Latitude'] = latitude
            formData['Longitude'] = longitude
            this.form.option('formData', formData)
        } else {
            this.form.option('formData', {
                Latitude: latitude,
                Longitude: longitude
            })
        }
    }

    // #endregion

    // #region Select Popover

    private setPopoverOptions(formItems: IMRDataTypePanelFormItemOptions[]): void {
        this.popoverOptions = []
        this.popovers = []

        const options = formItems

        options
            .filter((element: IMRDataTypePanelFormItemOptions) => element.popoverOptions != null)
            .forEach((element: IMRDataTypePanelFormItemOptions) => {
                const currentId: string = IMRDataTypePanelController.SELECT_POPOVER_OPTIONS + element.key
                const selectGridInputFieldOptions: IMRSelectGridInputFieldOptions<any> = {
                    selectGridPopoverOptions: {
                        selectGridOptions: {
                            gridOptions: element.popoverOptions.gridOptions,

                            onObjectSelected: (e: IMRSelectGridObjectSelected<any>) => {
                                let found: boolean = false

                                this.popoverFormData = {
                                    object: e.selectedObject,
                                    id: currentId,
                                    key: element.key
                                }

                                if (this.popoverFormDataArray != null && this.popoverFormDataArray.length != 0) {
                                    for (let i = 0; i < this.popoverFormDataArray.length; i++) {
                                        if (this.popoverFormDataArray[i].id == this.popoverFormData.id) {
                                            this.popoverFormDataArray.splice(i, 1)
                                            this.popoverFormDataArray.push(this.popoverFormData)
                                            found = true
                                            break
                                        }
                                    }
                                }

                                if (!found) {
                                    this.popoverFormDataArray.push(this.popoverFormData)
                                }

                                this.onFieldValueChanged({
                                    controller: this,
                                    dataField: element.key.toString(),
                                    value: e.selectedObject.getReferenceKey()
                                })
                            }
                        },

                        closeAfterSelect: true,
                        preserveSelectionOnClose: true
                    },

                    isRequired: this.isOpDataTypeRequired(element.dataType, this.opDataTypes.required),

                    onInitialized: (e: IMRSelectGridInputFieldInitializedArgs<any>) => {
                        this.popovers[`${IMRDataTypePanelController.SELECT_POPOVER}${element.key}`] = e.component
                    }
                }

                this.popoverOptions[currentId] = selectGridInputFieldOptions
            })
    }

    // #endregion

    // #region DataTypes

    private createIOpData(opDataType: OpDataType, value: any, opState: Enums.OpChangeState = Enums.OpChangeState.New): IOpData {
        let obj: IOpData

        if (this.options.factory == null) {
            obj = {
                idDataType: opDataType.idDataType,
                indexNBR: 0,
                idData: 0,
                opState,
                value,

                dataType: opDataType,

                toJSON() { },
                fromJSON() { }
            }
        } else {
            obj = new this.options.factory(null)

            obj.idData = 0
            obj.idDataType = opDataType.idDataType
            obj.indexNBR = 0
            obj.value = value
            obj.dataType = opDataType
            obj.opState = opState
        }

        return obj
    }

    private getOpDataTypes(): JQueryPromise<OpDataType[][]> {
        if (this.opDataTypes != null) {
            return $
                .Deferred<[OpDataType[], OpDataType[]]>()
                .resolve([this.opDataTypes.params || [], this.opDataTypes.required || []])
                .promise()
        }
        else if (this.dataTypes != null) {
            return $
                .when<OpDataType[]>(
                    this.userSessionService.getDataProvider().DataType.getDataTypes(this.dataTypes.params),
                    this.userSessionService.getDataProvider().DataType.getDataTypes(this.dataTypes.required)
                )
                .then((params: OpDataType[], required: OpDataType[]) => {
                    return [params || [], required || []]
                })
        }
        else if (this.dataTypeGroups != null) {
            return $
                .when<OpDataTypeInGroup[]>(
                    this.getOpDataTypesByDataTypeGroup(this.dataTypeGroups.params),
                    this.getOpDataTypesByDataTypeGroup(this.dataTypeGroups.required)
                )
                .then((params: OpDataTypeInGroup[], required: OpDataTypeInGroup[]) => {
                    return [
                        _.map(params, (dataTypeInGroup: OpDataTypeInGroup) => dataTypeInGroup.dataType),
                        _.map(required, (dataTypeInGroup: OpDataTypeInGroup) => dataTypeInGroup.dataType),
                    ]
                })
        }

        return $
            .Deferred<[OpDataType[], OpDataType[]]>()
            .resolve([[], []])
            .promise()
    }

    private getOpDataTypesByDataTypeGroup(dataTypeGroup: Enums.DataTypeGroup): JQueryPromise<OpDataTypeInGroup[]> {
        if (dataTypeGroup != null) {
            return this.userSessionService.getDataProvider().DataTypeGroup.getDataTypeInGroup(dataTypeGroup.toString())
        }

        return $.Deferred<OpDataTypeInGroup[]>().resolve([]).promise()
    }

    private getOpDataTypeAdditionalOptions(opDataType: OpDataType): IMRDataTypePanelAdditionalOptions {
        if (this.options != null && this.options.additionalOptions != null) {
            return this.options.additionalOptions[opDataType.idDataType]
        }

        return null
    }

    private isOpDataTypeRequired(opDataType: OpDataType, required: OpDataType[]): boolean {
        if (opDataType != null) {
            return _.find(required, (requiredDataType: OpDataType) => requiredDataType.idDataType === opDataType.idDataType) != null
        }

        return false
    }

    private getOpDataTypeData(opDataType: OpDataType): any[] {
        if (opDataType != null && this.dataList != null) {
            return _.map(this.dataList.getDataList(opDataType.idDataType), (data: IOpData) => data.value)
        }

        return null
    }

    // #endregion

    // #region Setup

    private initialize(): void {
        this.isLoading = true

        this.setOptions()

        this.reset()
            .then(() => {
                this.onInitialized()
            })
            .always(() => {
                this.isLoading = false
            })
    }


    // #endregion

    // #region Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    private onFieldValueChanged(args: IMRDataTypePanelFieldValueChanged): void {
        if (this.options != null && _.isFunction(this.options.onFieldValueChanged)) {
            this.options.onFieldValueChanged(args)
        }
    }

    private onFormContentReady(): void {
        if (this.options != null && _.isFunction(this.options.onFormContentReady)) {
            this.options.onFormContentReady(this)
        }
    }

    // #endregion

    // #region Options

    private setOptions(): void {
        if (this.options != null) {
            this.setDataList(this.options.dataList)
            this.setOpDataTypes(this.options.opDataTypes)
            this.setDataTypes(this.options.idDataTypes)
            this.setDataTypeGroups(this.options.dataTypeGroups)
        }
    }

    // #endregion
}
